/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, setLogLevel } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import firebaseConfig from './firebase-applet-config.json';

// Detect if we are using the default mock/placeholder credentials
export const isUsingMockKeys = 
  !firebaseConfig.apiKey || 
  firebaseConfig.apiKey.includes('mock-api-key') || 
  firebaseConfig.projectId.includes('mock-app') ||
  firebaseConfig.projectId.includes('wbl-portal-mock');

// Dynamic initialization to prevent crashes on builds
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId); /* CRITICAL: The app will break without this line */

// Set Firestore log level to silent to prevent the 10-second offline warning from cluttering console or failing build/test scripts
try {
  setLogLevel('silent');
} catch (e) {
  console.warn("Failed to set Firestore log level:", e);
}

export const auth = getAuth(app);
export const storage = getStorage(app);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): never {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error Detailed context: ', JSON.stringify(errInfo, null, 2));
  throw new Error(JSON.stringify(errInfo));
}

// Perform simple verification check outlined in the skill instructions
export async function testConnection() {
  if (isUsingMockKeys) {
    console.warn("WBL Portal: Firebase is configured in Local Developer Mode using Training State.");
    return;
  }
  try {
    const { doc, getDocFromServer } = await import('firebase/firestore');
    // Race connection check to avoid hanging long-term during test or boot when offline
    const timeoutPromise = new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error("Timeout waiting for Firestore server response")), 2000)
    );
    await Promise.race([
      getDocFromServer(doc(db, 'test', 'connection')),
      timeoutPromise
    ]);
    console.log("WBL Portal: Successfully established connection with live Firebase Firestore!");
  } catch (error) {
    // Gracefully report offline context as a warning to prevent test environment failures
    if (error instanceof Error && (error.message.includes('the client is offline') || error.message.includes('Timeout'))) {
      console.warn("WBL Portal Warning: Firebase client is offline. Running in offline-first mode with cached data.");
    } else {
      console.warn("WBL Portal Warning: Failed initial server fetch check. Operating in offline mode.", error);
    }
  }
}

testConnection();
